import { gql } from "graphql-request";
import { useGraphqlMutation, useGraphqlQuery } from "@/lib/graphql/useGraphql";

export type AdminInviteMethod = "NONE" | "PASSWORD_RESET" | "SIGNUP_INVITE";

export type AdminUserDetail = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: string;
  timezone: string | null;
  jobtitle: string | null;
  company: string | null;
  org_id: string;
  org_name: string;
  workos_user_id: string | null;
  stripe_customer_id: string | null;
  hubspot_contact_id: string | null;
  last_login_at: string | null;
  last_activity_at: string | null;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type AdminBulkCreateUserRowResult = {
  row_number: number;
  email: string;
  success: boolean;
  user_id: string | null;
  errors: string[];
};

export type AdminBulkCreateUsersResult = {
  created_count: number;
  failed_count: number;
  rows: AdminBulkCreateUserRowResult[];
};

export type AdminCreateUserInput = {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  org_id?: string | null;
  hubspot_contact_id?: string | null;
  timezone?: string | null;
  jobtitle?: string | null;
  invite?: boolean;
  invite_method?: AdminInviteMethod;
  invited_by_email?: string | null;
  credits_usd?: number | null;
};

export type AdminBulkUserInput = AdminCreateUserInput & { row_number?: number };

const ADMIN_USER_QUERY = gql`
  query AdminUser($id: ID!) {
    adminUser(id: $id) {
      id
      email
      first_name
      last_name
      status
      timezone
      jobtitle
      company
      org_id
      org_name
      workos_user_id
      stripe_customer_id
      hubspot_contact_id
      last_login_at
      last_activity_at
      email_verified_at
      created_at
      updated_at
      deleted_at
    }
  }
`;

const ADMIN_CREATE_USER = gql`
  mutation AdminCreateUser(
    $email: String!
    $first_name: String
    $last_name: String
    $company: String
    $org_id: ID
    $hubspot_contact_id: ID
    $timezone: String
    $jobtitle: String
    $invite: Boolean
    $invite_method: AdminInviteMethod
    $invited_by_email: String
    $credits_usd: Int
  ) {
    adminCreateUser(
      email: $email
      first_name: $first_name
      last_name: $last_name
      company: $company
      org_id: $org_id
      hubspot_contact_id: $hubspot_contact_id
      timezone: $timezone
      jobtitle: $jobtitle
      invite: $invite
      invite_method: $invite_method
      invited_by_email: $invited_by_email
      credits_usd: $credits_usd
    ) {
      id
      email
      first_name
      last_name
      status
    }
  }
`;

const ADMIN_UPDATE_USER = gql`
  mutation AdminUpdateUser(
    $id: ID!
    $first_name: String
    $last_name: String
    $status: String
    $timezone: String
    $jobtitle: String
    $company: String
  ) {
    adminUpdateUser(
      id: $id
      first_name: $first_name
      last_name: $last_name
      status: $status
      timezone: $timezone
      jobtitle: $jobtitle
      company: $company
    ) {
      id
      email
      first_name
      last_name
      status
      timezone
      jobtitle
      company
      deleted_at
    }
  }
`;

const ADMIN_SOFT_DELETE_USER = gql`
  mutation AdminSoftDeleteUser($id: ID!) {
    adminSoftDeleteUser(id: $id) {
      id
      deleted_at
      status
    }
  }
`;

const ADMIN_RESTORE_USER = gql`
  mutation AdminRestoreUser($id: ID!) {
    adminRestoreUser(id: $id) {
      id
      deleted_at
      status
    }
  }
`;

const ADMIN_FORCE_DELETE_USER = gql`
  mutation AdminForceDeleteUser($id: ID!, $email_confirmation: String!) {
    adminForceDeleteUser(id: $id, email_confirmation: $email_confirmation)
  }
`;

const ADMIN_BULK_CREATE_USERS = gql`
  mutation AdminBulkCreateUsers($rows: [AdminBulkUserInput!]!) {
    adminBulkCreateUsers(rows: $rows) {
      created_count
      failed_count
      rows {
        row_number
        email
        success
        user_id
        errors
      }
    }
  }
`;

const ADMIN_EMAIL_EXISTS_QUERY = gql`
  query AdminEmailExists($email: String!) {
    adminEmailExists(email: $email)
  }
`;

const ADMIN_EMAILS_EXIST_QUERY = gql`
  query AdminEmailsExist($emails: [String!]!) {
    adminEmailsExist(emails: $emails)
  }
`;

export const useAdminEmailsExist = (emails: string[], enabled = true) =>
  useGraphqlQuery<{ adminEmailsExist: string[] }, { emails: string[] }>({
    queryKey: ["adminEmailsExist", emails],
    document: ADMIN_EMAILS_EXIST_QUERY,
    variables: { emails },
    enabled: enabled && emails.length > 0,
  });

const ADMIN_COMPANY_NAME_SUGGESTIONS_QUERY = gql`
  query AdminCompanyNameSuggestions {
    adminCompanyNameSuggestions
  }
`;

export const useAdminEmailExists = (email: string, enabled = true) =>
  useGraphqlQuery<{ adminEmailExists: boolean }, { email: string }>({
    queryKey: ["adminEmailExists", email],
    document: ADMIN_EMAIL_EXISTS_QUERY,
    variables: { email },
    enabled: enabled && email.length > 0,
  });

export const useAdminCompanyNameSuggestions = (enabled = true) =>
  useGraphqlQuery<{ adminCompanyNameSuggestions: string[] }>({
    queryKey: ["adminCompanyNameSuggestions"],
    document: ADMIN_COMPANY_NAME_SUGGESTIONS_QUERY,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min browser-side cache
  });

export const useAdminUser = (id: string | null, enabled = true) =>
  useGraphqlQuery<{ adminUser: AdminUserDetail }, { id: string }>({
    queryKey: ["adminUser", id],
    document: ADMIN_USER_QUERY,
    variables: id ? { id } : undefined,
    enabled: enabled && Boolean(id),
  });

export const useAdminCreateUser = () =>
  useGraphqlMutation<{ adminCreateUser: AdminUserDetail }, AdminCreateUserInput>({
    document: ADMIN_CREATE_USER,
  });

export const useAdminUpdateUser = () =>
  useGraphqlMutation<
    { adminUpdateUser: AdminUserDetail },
    {
      id: string;
      first_name?: string | null;
      last_name?: string | null;
      status?: string;
      timezone?: string | null;
      jobtitle?: string | null;
      company?: string | null;
    }
  >({ document: ADMIN_UPDATE_USER });

export const useAdminSoftDeleteUser = () =>
  useGraphqlMutation<{ adminSoftDeleteUser: AdminUserDetail }, { id: string }>({
    document: ADMIN_SOFT_DELETE_USER,
  });

export const useAdminRestoreUser = () =>
  useGraphqlMutation<{ adminRestoreUser: AdminUserDetail }, { id: string }>({
    document: ADMIN_RESTORE_USER,
  });

export const useAdminForceDeleteUser = () =>
  useGraphqlMutation<{ adminForceDeleteUser: boolean }, { id: string; email_confirmation: string }>({
    document: ADMIN_FORCE_DELETE_USER,
  });

export const useAdminBulkCreateUsers = () =>
  useGraphqlMutation<{ adminBulkCreateUsers: AdminBulkCreateUsersResult }, { rows: AdminBulkUserInput[] }>({
    document: ADMIN_BULK_CREATE_USERS,
  });

const ADMIN_SYNC_WORKOS_USER_ID = gql`
  mutation AdminSyncWorkosUserId($id: ID!) {
    adminSyncWorkosUserId(id: $id) {
      id
      workos_user_id
    }
  }
`;

export const useAdminSyncWorkosUserId = () =>
  useGraphqlMutation<{ adminSyncWorkosUserId: { id: string; workos_user_id: string | null } }, { id: string }>({
    document: ADMIN_SYNC_WORKOS_USER_ID,
  });

export const ADMIN_USERS_CSV_TEMPLATE_PATH = "/templates/admin-import-users-template.csv";

export const downloadAdminUsersCsvTemplate = () => {
  const link = document.createElement("a");
  link.href = ADMIN_USERS_CSV_TEMPLATE_PATH;
  link.download = "admin-import-users-template.csv";
  link.click();
};

export type ParsedCsvRow = AdminBulkUserInput & { row_number: number };

const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());

  return result;
};

export const parseAdminUsersCsv = (text: string): { rows: ParsedCsvRow[]; errors: string[] } => {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    return { rows: [], errors: ["CSV file is empty."] };
  }

  const headerCells = parseCsvLine(lines[0]).map((h) => {
    const lower = h.toLowerCase();
    return lower === "org_id" ? "org_id" : lower;
  });
  if (!headerCells.includes("email")) {
    return { rows: [], errors: ["CSV must include an email column."] };
  }

  const errors: string[] = [];

  const rows: ParsedCsvRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    const record: Record<string, string> = {};
    headerCells.forEach((header, idx) => {
      record[header] = cells[idx] ?? "";
    });

    const email = (record.email ?? "").trim();
    if (email === "") {
      errors.push(`Row ${i}: email is required.`);
      continue;
    }

    // invite_user column: "signup_invite" → invitation link, "password_reset" → password email, blank → no invite
    const inviteRaw = (record.invite_user ?? "").toLowerCase().trim();
    const VALID_INVITE_VALUES = ["signup_invite", "password_reset", ""];
    if (!VALID_INVITE_VALUES.includes(inviteRaw)) {
      errors.push(
        `Row ${i}: invalid invite_user value "${record.invite_user}". Use "signup_invite", "password_reset", or leave blank.`,
      );
      continue;
    }
    let invite = false;
    let inviteMethod: AdminInviteMethod = "NONE";
    if (inviteRaw === "signup_invite") {
      invite = true;
      inviteMethod = "SIGNUP_INVITE";
    } else if (inviteRaw === "password_reset") {
      invite = true;
      inviteMethod = "PASSWORD_RESET";
    }

    const creditsRaw = (record.credits_usd ?? "").trim();
    let creditsUsd: number | null = null;
    if (creditsRaw !== "") {
      if (!/^\d+$/.test(creditsRaw)) {
        errors.push(`Row ${i}: credits_usd must be a whole non-negative integer (no decimals or special characters).`);
        continue;
      }
      creditsUsd = parseInt(creditsRaw, 10);
    }

    rows.push({
      row_number: i,
      email,
      first_name: record.first_name || null,
      last_name: record.last_name || null,
      company: record.company || null,
      jobtitle: record.jobtitle || null,
      invite,
      invite_method: inviteMethod,
      // blank → backend falls back to seyum@radium.cloud default
      invited_by_email: record.invited_by || null,
      hubspot_contact_id: record.hubspot_contact_id || null,
      org_id: record.org_id || null,
      timezone: record.timezone || null, // accepted for backward compat, ignored by backend
      credits_usd: creditsUsd,
    });
  }

  return { rows, errors };
};
